import React from 'react';
import { ConfirmExitPrompt } from 'components/shared/navigation/ConfirmExitPrompt/ConfirmExitPrompt';

import { Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Form } from 'components/shared/form';
import { RadioList } from 'components/shared/form/RadioBox/RadioList';
import { Button } from 'components/shared/buttons/Button/Button';
import { Splash } from 'components/shared/surfaces/Splash/Splash';
import { TextLoader } from 'components/shared/loaders/TextLoader/TextLoader';
import { useNotification, useTranslator } from 'components/shared/hooks';
import {
  PasswordSecurityFormValues,
  PasswordSecurityProps,
  PasswordSecurityReaction,
} from 'components/officials/PasswordSecurity/PasswordSecurity.types';

const LEVEL_OPTIONS = (trans) => [
  {
    value: 'default',
    label: trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.LEVEL.DEFAULT'),
  },
  {
    value: 'max',
    label: trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.LEVEL.MAX'),
  },
];

const REACTION_OPTIONS = (trans) => [
  {
    value: 'allow' as PasswordSecurityReaction,
    label: trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.REACTION.ALLOW_SKIP'),
  },
  {
    value: 'force' as PasswordSecurityReaction,
    label: trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.REACTION.FORCE_CHANGE'),
  },
];

export const PasswordSecurity: React.FC<PasswordSecurityProps> = ({
  initialValues,
  loading,
  submitted,
  savedFormValues,
  handleFormSubmit,
}) => {
  const trans = useTranslator();
  const { info } = useNotification();

  const handleRevertingFormChanges = (form: FormikProps<PasswordSecurityFormValues>) => {
    if (submitted && savedFormValues) {
      form.setValues(savedFormValues);
    } else {
      form.resetForm();
    }
    info('SETTINGS.FORM.UNDO_CHANGES');
  };

  if (loading) {
    return (
      <Splash>
        <TextLoader name="spinner" text={trans('COMMON.LOADING_DATA')} />
      </Splash>
    );
  }

  return (
    <Form<PasswordSecurityFormValues> initialValues={initialValues} onSubmit={handleFormSubmit} enableReinitialize={true}>
      {(form: FormikProps<PasswordSecurityFormValues>) => (
        <React.Fragment>
          <ConfirmExitPrompt
            when={(form.dirty && !submitted) || (!!savedFormValues && form.values !== savedFormValues)}
            message={trans('COMMON.CONFIRM_EXIT')}
          />
          <div className="mb-4">
            <Typography variant="h6">{trans('OFFICIALS.PASSWORD_SECURITY.SECTIONS.REQUIREMENTS')}</Typography>
            <div className="mt-3">
              <Typography variant="subtitle1">{trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.OFFICIAL')}</Typography>
              <RadioList name="petentLevel" options={LEVEL_OPTIONS(trans)} />
            </div>
            <div className="mt-3">
              <Typography variant="subtitle1">{trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.APPLICANT')}</Typography>
              <RadioList name="applicantLevel" options={LEVEL_OPTIONS(trans)} />
            </div>
          </div>

          <div className="mb-4">
            <Typography variant="h6">{trans('OFFICIALS.PASSWORD_SECURITY.SECTIONS.REACTION')}</Typography>
            <div className="mt-3">
              <Typography variant="subtitle1">{trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.OFFICIAL')}</Typography>
              <RadioList name="petentAllowSkip" options={REACTION_OPTIONS(trans)} />
            </div>
            <div className="mt-3">
              <Typography variant="subtitle1">{trans('OFFICIALS.PASSWORD_SECURITY.FIELDS.APPLICANT')}</Typography>
              <RadioList name="applicantAllowSkip" options={REACTION_OPTIONS(trans)} />
            </div>
          </div>

          <div className="row my-4">
            <div className="col">
              <Button
                onClick={() => handleRevertingFormChanges(form)}
                color="secondary"
                variant="outlined"
              >
                {trans('COMMON.CANCEL')}
              </Button>
              <Button
                onClick={() => form.handleSubmit()}
                disabled={form.isSubmitting}
                color="secondary"
                variant="contained"
                className="ml-3"
              >
                {trans('COMMON.SAVE')}
              </Button>
            </div>
          </div>
        </React.Fragment>
      )}
    </Form>
  );
};
